import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import PageHeader from '../../components/PageHeader.jsx';
import Empty from '../../components/Empty.jsx';
import Modal from '../../components/Modal.jsx';
import { templatesApi } from '../../api/templates.js';
import { sitesApi, pagesApi } from '../../api/sites.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { apiError } from '../../api/client.js';

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyTo, setApplyTo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    templatesApi
      .list()
      .then((tpls) => !cancelled && setTemplates(tpls))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    const out = {};
    templates.forEach((t) => {
      out[t.category] = out[t.category] || [];
      out[t.category].push(t);
    });
    return out;
  }, [templates]);

  return (
    <>
      <PageHeader
        title="Templates"
        description="Start a page from a curated layout. Tweak it freely after applying."
      />
      <div className="px-8 py-8">
        {loading ? (
          <div className="text-sm text-ink-500">Loading templates…</div>
        ) : templates.length === 0 ? (
          <Empty
            title="No templates yet"
            description="Run the Laravel seeders (php artisan db:seed) to populate the gallery."
          />
        ) : (
          Object.entries(grouped).map(([category, list]) => (
            <section key={category} className="mb-10">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-500">
                {category}
              </h2>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {list.map((t) => (
                  <article key={t.id} className="card flex flex-col p-5">
                    <div className="mb-4 grid h-32 place-items-center rounded-lg bg-gradient-to-br from-brand-100 to-ink-100 text-sm font-semibold text-brand-700 dark:from-ink-800 dark:to-ink-900 dark:text-ink-300">
                      {t.name}
                    </div>
                    <h3 className="text-base font-semibold">{t.name}</h3>
                    <p className="mt-1 flex-1 text-sm text-ink-500">{t.description}</p>
                    <button
                      onClick={() => setApplyTo(t)}
                      className="btn-secondary mt-4 self-start"
                    >
                      Apply to a page
                    </button>
                  </article>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
      <ApplyTemplateModal
        template={applyTo}
        onClose={() => setApplyTo(null)}
      />
    </>
  );
}

function ApplyTemplateModal({ template, onClose }) {
  const { workspace } = useAuth();
  const [sites, setSites] = useState([]);
  const [pages, setPages] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [pageId, setPageId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!template || !workspace) return;
    sitesApi.list(workspace.id).then(setSites).catch((e) => toast.error(apiError(e)));
  }, [template, workspace]);

  useEffect(() => {
    if (!siteId) {
      setPages([]);
      setPageId('');
      return;
    }
    pagesApi
      .list(siteId)
      .then((p) => {
        setPages(p);
        setPageId(p[0]?.id || '');
      })
      .catch((e) => toast.error(apiError(e)));
  }, [siteId]);

  async function apply() {
    if (!template || !pageId) return;
    setBusy(true);
    try {
      await templatesApi.applyToPage(template.id, pageId);
      toast.success('Template applied to page draft');
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={!!template}
      onClose={onClose}
      title={template ? `Apply "${template.name}"` : ''}
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={apply} className="btn-primary" disabled={busy || !pageId}>
            {busy ? 'Applying…' : 'Apply template'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label">Site</label>
          <select className="input" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">Pick a site…</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Page (will replace draft blocks)</label>
          <select
            className="input"
            value={pageId}
            onChange={(e) => setPageId(e.target.value)}
            disabled={!siteId}
          >
            <option value="">Pick a page…</option>
            {pages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-500">
            The template overwrites the page's <span className="font-mono">draft_blocks</span>.
            Publish from the editor to make it live.
          </p>
        </div>
      </div>
    </Modal>
  );
}
